import { useEffect, useRef } from 'react';
import { FormikProps } from 'formik';
import { EventProductType, OutgoingEventProductDto } from 'api/dtos/events/products';
import computeInitialValues, { FormValueDto } from '../helpers/computeInitialValues';

/**
 * Hook to handle product type changes in the form
 * @param formikRef Reference to the Formik instance
 * @param ticket Existing ticket being edited (if any)
 * @param isTierPrivate Whether this is a private tier
 */
const useProductTypeChange = (
  formikRef: React.RefObject<FormikProps<FormValueDto>>,
  ticket?: OutgoingEventProductDto,
  isTierPrivate = false
) => {
  const isEditingTicket = !!ticket;
  const previousTypeRef = useRef<EventProductType | null>(null);

  useEffect(() => {
    // Handle special case for editing a bundle product
    if (isEditingTicket && ticket && ticket.type === 'bundle') {
      // Initialize components if not already set
      if (!formikRef.current?.values?.components || !formikRef.current.values.components.length) {
        formikRef.current?.setFieldValue('components', ticket.components || []);
      }
      
      // Set type explicitly
      if (!formikRef.current?.values?.type) {
        formikRef.current?.setFieldValue('type', 'bundle');
      }
    }
  }, [isEditingTicket, ticket]);

  // Set up a listener for type changes
  const handleTypeChange = () => {
    // Skip when editing or if formik reference isn't ready
    if (!formikRef.current || isEditingTicket) return;
    
    const currentType = formikRef.current.values.type as EventProductType;
    
    // Skip if type hasn't changed
    if (currentType === previousTypeRef.current) return;
    
    // Update the previous type reference
    previousTypeRef.current = currentType;
    
    // Don't reset if validation errors exist
    if (Object.keys(formikRef.current.errors).length > 0) return;
    
    // Get new initial values based on the selected product type
    const newValues = computeInitialValues(null, isTierPrivate, currentType);
    
    // Preserve common fields that user may have already entered
    const commonFields = ['name', 'description', 'price', 'isPrivate', 'isActive'];
    commonFields.forEach(field => {
      if (formikRef.current.values[field]) {
        newValues[field] = formikRef.current.values[field];
      }
    });
    
    // For bundle type, ensure components array is initialized
    if (currentType === 'bundle') {
      newValues.components = newValues.components || [];
    }
    
    // Reset the form with the new values
    formikRef.current.resetForm({ values: newValues });
  };

  // Watch for changes to the type field
  useEffect(() => {
    if (!formikRef.current || isEditingTicket) return;
    
    // Add a listener for form values change
    const formik = formikRef.current;
    
    // Set up a manual watcher for type changes
    const watchTypeChanges = () => {
      const type = formik.values.type;
      if (type !== previousTypeRef.current) {
        handleTypeChange();
      }
    };
    
    formik.setFieldValue('type', formik.values.type, false);
    formik.validateField('type');
    
    // Watch for changes to form values
    const intervalId = setInterval(watchTypeChanges, 300);
    
    return () => {
      clearInterval(intervalId);
    };
  }, [formikRef.current, isEditingTicket]);

  return { handleTypeChange };
};

export default useProductTypeChange; 